// src/pages/Home/LastNews.jsx (par exemple)
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getLatestNews } from "./../../services/newsServices";
import { FaArrowRight, FaTimes } from "react-icons/fa";

const NEWS_PER_PAGE = 3;

const LatestNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const [selectedNews, setSelectedNews] = useState(null);
  const [zoomImage, setZoomImage] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const latestNews = await getLatestNews();
        const activeNews = (latestNews || []).filter((news) => news.is_active);
        setNewsList(activeNews);
      } catch (error) {
        console.error("Failed to fetch news", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(newsList.length / NEWS_PER_PAGE)),
    [newsList.length]
  );

  const newsToDisplay = useMemo(() => {
    if (!newsList.length) return [];
    const start = page * NEWS_PER_PAGE;
    return newsList.slice(start, start + NEWS_PER_PAGE);
  }, [newsList, page]);

  const handleReadMore = () => {
    navigate("/actualites");
  };

  const goNextPage = () => {
    setPage((prev) => (prev + 1) % pageCount);
  };

  const goPrevPage = () => {
    setPage((prev) => (prev - 1 + pageCount) % pageCount);
  };

  const formatPublishedAt = (news) => {
    const rawDate =
      news.date_news ||
      news.date_publication ||
      news.createdAt ||
      news.created_at;
    if (!rawDate) return null;
    return new Date(rawDate).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const openModal = (news) => {
    setSelectedNews(news);
    setZoomImage(false);
  };

  const closeModal = () => {
    setSelectedNews(null);
    setZoomImage(false);
  };

  // 🔒 Empêche les liens de la preview d'ouvrir une nouvelle page
  const handlePreviewContentClick = (e) => {
    const target = e.target;
    if (target && target.closest("a")) {
      e.preventDefault(); // pas de navigation
      // on ne fait PAS stopPropagation -> le clic continue jusqu'à la card -> ouvre la modale
    }
  };

  /* ========== Loading ========== */
  if (loading) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-6 md:mb-8 animate-pulse">
            <div className="h-6 w-64 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
            <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 h-64"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ========== Pas d’actus ========== */
  if (!newsList.length) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
            Actualités
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">
            Dernières actualités
          </h2>
          <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
            Aucune actualité publiée pour le moment. Revenez bientôt !
          </p>
        </div>
      </section>
    );
  }

  /* ========== Affichage normal ========== */
  return (
    <>
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Titre */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6 md:mb-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                Actualités
              </p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                Dernières actualités du comité
              </h2>
            </div>
            <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 max-w-md">
              Informations, décisions et temps forts de la vie du Judo girondin.
            </p>
          </div>

          {/* Cartes : 3 par page */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {newsToDisplay.map((news) => {
              const image =
                news.image_url && news.image_url !== ""
                  ? news.image_url
                  : "/img/default_image.jpg";
              const authorName = `${news.author?.prenom || ""} ${
                news.author?.nom || ""
              }`.trim();
              const publishedAt = formatPublishedAt(news);

              return (
                <article
                  key={news.id}
                  onClick={() => openModal(news)}
                  className="group flex flex-col bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={image}
                      alt={news.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent pointer-events-none" />

                    {/* Date si dispo */}
                    {publishedAt && (
                      <div className="absolute left-3 bottom-3 bg-white/95 rounded-full px-3 py-1 text-[11px] font-semibold text-slate-900 shadow-md">
                        Publié le {publishedAt}
                      </div>
                    )}
                  </div>

                  {/* Contenu */}
                  <div className="flex flex-col flex-grow p-4 space-y-3">
                    <h3 className="text-base md:text-lg font-semibold text-slate-900 dark:text-slate-50 line-clamp-2">
                      {news.title}
                    </h3>

                    {authorName && (
                      <p className="inline-flex items-center justify-start text-[11px] font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/70 rounded-full px-3 py-1">
                        {authorName}
                      </p>
                    )}

                    {news.content && (
                      <div
                        className="text-xs md:text-sm text-slate-700 dark:text-slate-300 line-clamp-5"
                        onClick={handlePreviewContentClick}
                        dangerouslySetInnerHTML={{ __html: news.content }}
                      />
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {/* Pagination + CTA global */}
          <div className="mt-6 md:mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Pagination pages de 3 */}
            {pageCount > 1 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={goPrevPage}
                  className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  ◀ Précédentes
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: pageCount }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setPage(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === page
                          ? "bg-primary"
                          : "bg-slate-300 dark:bg-slate-600"
                      }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={goNextPage}
                  className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Suivantes ▶
                </button>
              </div>
            )}

            {/* CTA voir toutes les actus */}
            <button
              type="button"
              onClick={handleReadMore}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary text-primary text-xs md:text-sm font-semibold uppercase tracking-[0.18em] bg-white dark:bg-slate-900 hover:bg-primary hover:text-white transition-colors"
            >
              <span>Voir toutes les actualités</span>
              <FaArrowRight className="text-sm" />
            </button>
          </div>
        </div>
      </section>

      {/* MODALE NEWS */}
      {selectedNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4">
          {/* fond sombre */}
          <div
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
            onClick={closeModal}
          />
          {/* contenu modale */}
          <div className="relative max-w-3xl w-full max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* bouton fermer */}
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-900/80 text-slate-100 hover:bg-slate-800"
              aria-label="Fermer"
            >
              <FaTimes className="w-4 h-4" />
            </button>

            <div className="flex flex-col max-h-[90vh]">
              {/* Image avec zoom */}
              <div
                className={`relative bg-black ${
                  zoomImage ? "max-h-[70vh]" : "max-h-72 sm:max-h-80"
                } overflow-hidden`}
              >
                <img
                  src={
                    selectedNews.image_url && selectedNews.image_url !== ""
                      ? selectedNews.image_url
                      : "/img/default_image.jpg"
                  }
                  alt={selectedNews.title}
                  className={`w-full h-full ${
                    zoomImage ? "object-contain" : "object-cover"
                  } ${zoomImage ? "cursor-zoom-out" : "cursor-zoom-in"}`}
                  onClick={() => setZoomImage((z) => !z)}
                />
                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[11px] px-3 py-1 rounded-full">
                  Cliquer pour {zoomImage ? "réduire" : "agrandir"}
                </div>
              </div>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <div className="mb-3">
                  <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                    Actualité du comité
                  </p>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
                    {selectedNews.title}
                  </h3>
                </div>

                {/* Meta auteur + date */}
                <div className="flex flex-wrap items-center gap-2 mb-4 text-xs sm:text-[13px] text-slate-600 dark:text-slate-300">
                  {selectedNews.author && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800/80">
                      {selectedNews.author.prenom}{" "}
                      {selectedNews.author.nom?.toUpperCase()}
                    </span>
                  )}
                  {formatPublishedAt(selectedNews) && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-50 dark:bg-slate-800/80">
                      Publié le {formatPublishedAt(selectedNews)}
                    </span>
                  )}
                </div>

                {/* Contenu complet (ici les liens restent cliquables) */}
                {selectedNews.content && (
                  <div
                    className="prose prose-sm sm:prose-base max-w-none prose-headings:mt-4 prose-a:text-primary dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: selectedNews.content }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LatestNews;
